import React, { useMemo, useState } from 'react';
import Select from '../../components/Select';
import { KpiRow, Panel, BarList, Empty, Segmented, StatusIcon, STATUS, StatusLegend, SortTh, sortRows, usePersisted, plural, fmtRub, fmtPct, fmtNum } from './charts';

// «Товары» (UX 15.10, «сложно — нужно проще»): три режима, у каждого своя дата.
// «Прибыль по позициям» и «Списания» — за период; «Остатки» — на сегодня (выбор периода спрятан).
// Фильтр везде один: поиск + «Показать: всё / букеты / стебли» + «Категория» (владелец 15.10).
// Построчный журнал списаний — в «Склад → Списания». Без выгрузки в Excel (13.10).

const COLLAPSED = 15;

function Flag({ status, text }) {
  if (!status) return null;
  const st = STATUS[status];
  return <span className={`an-flag ${st.cls}`} role="img" aria-label={`${st.word}: ${text || ''}`} title={text || st.word}><StatusIcon status={status} size={12} /></span>;
}

const ddmm = (iso) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}` : '');
const norm = (v) => (v || '').toLowerCase().replace(/ё/g, 'е');
const daysText = (d) => {
  if (d == null) return '—';
  if (d < 1) return 'меньше дня';
  if (d > 60) return 'больше 2 месяцев';
  return plural(Math.round(d), 'день', 'дня', 'дней');
};
// Вторая строка позиции: что это, категория, служебная пометка склада («для композиций»)
const subline = (...parts) => parts.filter(Boolean).join(' · ');

/* ---------- Прибыль по позициям ---------- */

const PROFIT_SORTS = {
  gross: { get: (x) => x.gross },
  qty: { get: (x) => x.qty },
  revenue: { get: (x) => x.revenue },
  name: { get: (x) => x.name },
  written_off: { get: (x) => x.written_off || 0 },
  net: { get: (x) => (x.net == null ? x.gross : x.net) },
};

const VIEWS = [
  { value: 'all', label: 'Показать: всё' },
  { value: 'bouquet', label: 'Показать: букеты' },
  { value: 'stem', label: 'Показать: стебли' },
];
const catOptions = (cats) => [{ value: '', label: 'Категория: все' }, ...cats.map(c => ({ value: c, label: `Категория: ${c}` }))];
const uniqCats = (rows) => Array.from(new Set(rows.map(x => x.category).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'ru'));

function ProfitTable({ data }) {
  const [q, setQ] = useState('');
  const [cat, setCat] = usePersisted('nf_an_pos_cat', '');
  const [view, setView] = usePersisted('nf_an_pos_view2', 'all');
  const [sortRaw, setSort] = usePersisted('nf_an_pos_sort3', { key: 'gross', dir: 'desc' });
  const [expanded, setExpanded] = useState(false);
  // Что продавали или списывали: лежащее без движения — в «Остатках»
  const source = useMemo(() => data.positions.filter(x => x.revenue > 0 || x.qty > 0 || x.written_off > 0), [data.positions]);
  const cats = useMemo(() => uniqCats(source), [source]);
  const filtered = useMemo(() => {
    const needle = norm(q.trim());
    return source.filter(x => {
      if (needle && !norm(x.name).includes(needle)) return false;
      if (cat && x.category !== cat) return false;
      if ((view === 'bouquet' || view === 'stem') && x.kind !== view) return false;
      return true;
    });
  }, [source, q, cat, view]);
  // «Списано» и «Итог» — только когда в отборе есть списания: иначе колонка из «—», а итог = прибыль
  const hasWo = filtered.some(x => x.written_off);
  const sort = useMemo(() => (!hasWo && (sortRaw.key === 'written_off' || sortRaw.key === 'net') ? { key: 'gross', dir: 'desc' } : sortRaw), [hasWo, sortRaw]);
  const rows = useMemo(() => sortRows(filtered, sort, PROFIT_SORTS), [filtered, sort]);
  const shown = expanded ? rows : rows.slice(0, COLLAPSED);
  const sum = rows.reduce((a, x) => ({ revenue: a.revenue + x.revenue, gross: a.gross + x.gross, wo: a.wo + (x.written_off || 0) }), { revenue: 0, gross: 0, wo: 0 });
  const sumMargin = sum.revenue ? sum.gross / sum.revenue * 100 : null;
  return (
    <>
      <div className="an-filters">
        <input className="form-input an-search" placeholder="Найти позицию" value={q} onChange={(e) => { setQ(e.target.value); setExpanded(false); }} />
        <span className="an-sel-wrap"><Select value={view} onChange={(v) => { setView(v); setExpanded(false); }} options={VIEWS} /></span>
        <span className="an-sel-wrap"><Select value={cat} onChange={(v) => { setCat(v); setExpanded(false); }} options={catOptions(cats)} /></span>
      </div>
      {rows.length ? (
        <>
          <div className="an-scroll-x">
            <table className="an-table an-fixed">
              <colgroup>
                <col style={{ width: hasWo ? '32%' : '40%' }} /><col className="an-m-hide" style={{ width: hasWo ? '10%' : '14%' }} />
                <col className="an-m-hide" style={{ width: hasWo ? '15%' : '22%' }} /><col style={{ width: hasWo ? '17%' : '24%' }} />
                {hasWo && <><col className="an-m-hide" style={{ width: '12%' }} /><col style={{ width: '14%' }} /></>}
              </colgroup>
              <thead><tr>
                <SortTh k="name" sort={sort} setSort={setSort} className="">Позиция</SortTh>
                <SortTh k="qty" sort={sort} setSort={setSort} className="r an-m-hide"
                  hint="Стебли — поштучно, букеты — целиком. Стебли, ушедшие в букеты, здесь не считаются — они в строке букета">Продано</SortTh>
                <SortTh k="revenue" sort={sort} setSort={setSort} className="r an-m-hide"
                  hint="Что заплатили покупатели за эту позицию — вместе с сервисным сбором, без оплаты бонусами">Выручка</SortTh>
                <SortTh k="gross" sort={sort} setSort={setSort}
                  hint="Выручка минус закупочная цена цветов. Упаковка, курьеры, комиссии не вычтены — они в «Финансах»">Прибыль</SortTh>
                {hasWo && <SortTh k="written_off" sort={sort} setSort={setSort} className="r an-m-hide"
                  hint="Списали за период по закупочной цене: увяли, брак, недостача">Списано</SortTh>}
                {hasWo && <SortTh k="net" sort={sort} setSort={setSort}
                  hint="Прибыль минус списания — сколько позиция принесла на самом деле">Итог</SortTh>}
              </tr></thead>
              <tbody>
                <tr className="an-total-row">
                  <td>Всего по отбору<div className="an-muted an-small">{plural(rows.length, 'позиция', 'позиции', 'позиций')}</div></td>
                  <td className="r an-m-hide">—</td>
                  <td className="r an-m-hide">{fmtRub(sum.revenue)}</td>
                  <td className={`r${sum.gross < 0 ? ' an-bad' : ''}`}>{fmtRub(sum.gross)}<div className="an-muted an-small">{sumMargin != null ? `маржа ${fmtPct(sumMargin, 0)}` : ' '}</div></td>
                  {hasWo && <td className="r an-m-hide">{sum.wo ? `−${fmtRub(sum.wo)}` : '—'}</td>}
                  {hasWo && <td className={`r${sum.gross - sum.wo < 0 ? ' an-bad' : ''}`}>{fmtRub(sum.gross - sum.wo)}</td>}
                </tr>
                {shown.map(x => (
                  <tr key={x.key} className={x.status ? `an-row-${x.status}` : ''}>
                    <td><div className="an-ellipsis" title={x.name}>{x.name}</div>
                      <div className="an-muted an-small an-ellipsis">{subline(x.kind === 'bouquet' && 'букет', x.category, x.tag, x.share > 0 && `${fmtNum(x.share, x.share < 1 ? 1 : 0)} % прибыли`)}</div></td>
                    <td className="r an-m-hide">{x.qty ? `${fmtNum(x.qty, 1)} шт` : '—'}</td>
                    <td className="r an-m-hide">{x.revenue ? fmtRub(x.revenue) : '—'}</td>
                    <td className={`r${hasWo ? '' : ' an-b'}${x.gross < 0 ? ' an-bad' : ''}`}>{x.revenue ? fmtRub(x.gross) : '—'}
                      <div className={`an-muted an-small${x.status ? ` an-cell ${STATUS[x.status].cls}` : ''}`}>
                        {x.margin != null ? <><Flag status={x.status} text={x.status_text} />маржа {fmtPct(x.margin, 0)}</> : ' '}
                      </div></td>
                    {hasWo && <td className="r an-m-hide">{x.written_off ? `−${fmtRub(x.written_off)}` : '—'}</td>}
                    {hasWo && <td className={`r an-b${(x.net ?? x.gross) < 0 ? ' an-bad' : ''}`}>{fmtRub(x.net ?? x.gross)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="an-list-foot">
            <span className="an-muted">Показано {shown.length} из {rows.length}</span>
            {rows.length > COLLAPSED && (
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setExpanded(e => !e)}>
                {expanded ? 'Свернуть' : `Показать все (${rows.length})`}
              </button>
            )}
          </div>
        </>
      ) : <Empty>Под отбор ничего не попало</Empty>}
      <div className="an-muted an-small" style={{ marginTop: 10 }}>Под позицией — её доля во всей прибыли за период.</div>
      <StatusLegend />
    </>
  );
}

/* ---------- Остатки (на сегодня) ---------- */

const STOCK_SORTS = {
  attention: { get: (x) => (x.expired ? 1e12 : 0) + (x.lost_cost || 0) * 1000 + (x.idle ? x.stock : 0) },
  stock: { get: (x) => x.stock },
  days: { get: (x) => (x.days_left == null ? Infinity : x.days_left) },
  name: { get: (x) => x.name },
};

function StockTable({ data, withRisk }) {
  const [q, setQ] = useState('');
  const [view, setView] = usePersisted('nf_an_stock_view', 'all');
  const [cat, setCat] = usePersisted('nf_an_stock_cat', '');
  const [sort, setSort] = usePersisted('nf_an_stock_sort', { key: 'attention', dir: 'desc' });
  const [expanded, setExpanded] = useState(false);
  const all = useMemo(() => {
    const risk = new Map((data.risk || []).map(r => [String(r.product_id), r]));
    const seen = new Set();
    const out = [];
    for (const x of data.positions) {
      // Стебли со свободным остатком или бронью; букеты — собранные, лежат на полке (15.10)
      const stem = x.kind === 'stem' && x.stock != null && (x.stock > 0 || x.reserved > 0);
      const bouquet = x.kind === 'bouquet' && x.assembled > 0;
      if (!stem && !bouquet) continue;
      const rk = bouquet ? `b${x.bouquet_id}` : String(x.product_id);
      const r = risk.get(rk);
      seen.add(rk);
      out.push({ key: x.key, kind: x.kind, name: x.name, category: x.category, tag: x.tag, hidden: x.hidden,
        stock: bouquet ? x.assembled : x.stock, reserved: bouquet ? 0 : (x.reserved || 0),
        days_left: bouquet ? null : x.days_left, until: r?.until || x.until, expired: !!r?.expired, lost: r?.lost || 0,
        lost_cost: r?.lost_cost || 0, soon: !!r?.soon, idle: stem && x.stock > 0 && x.days_left == null, bouquet });
    }
    // Собранные букеты под риском, которых нет в позициях, — отдельными строками
    for (const r of data.risk || []) {
      if (r.bouquet && !seen.has(String(r.product_id))) out.push({ key: String(r.product_id), kind: 'bouquet', name: r.name, category: 'Букеты',
        stock: r.qty, reserved: 0, days_left: null, until: r.until, expired: r.expired, lost: r.lost, lost_cost: r.lost_cost, idle: false, bouquet: true });
    }
    return out;
  }, [data.positions, data.risk]);
  const cats = useMemo(() => uniqCats(all), [all]);
  const rows = useMemo(() => {
    const needle = norm(q.trim());
    return sortRows(all.filter(x => {
      if (needle && !norm(x.name).includes(needle)) return false;
      if (cat && x.category !== cat) return false;
      if ((view === 'bouquet' || view === 'stem') && x.kind !== view) return false;
      return true;
    }), sort, STOCK_SORTS);
  }, [all, q, cat, view, sort]);
  const shown = expanded ? rows : rows.slice(0, COLLAPSED);
  // Три разные суммы — не складываем в одну (проверка 15.10): уже просрочено, не успеем до конца срока, собранные букеты
  const lost = all.reduce((a, x) => {
    if (x.bouquet) a.bouquets += x.lost_cost;
    else if (x.expired) a.expired += x.lost_cost;
    else a.soon += x.lost_cost;
    if ((x.expired || x.lost > 0) && !x.lost_cost) a.noPrice += 1;
    return a;
  }, { expired: 0, soon: 0, bouquets: 0, noPrice: 0 });
  const lostParts = [lost.expired && `срок вышел — ${fmtRub(lost.expired)}`, lost.soon && `не успеем до конца срока — ${fmtRub(lost.soon)}`,
    lost.bouquets && `собранные букеты — ${fmtRub(lost.bouquets)}`].filter(Boolean);
  return (
    <>
      <div className="an-filters">
        <input className="form-input an-search" placeholder="Найти позицию" value={q} onChange={(e) => { setQ(e.target.value); setExpanded(false); }} />
        <span className="an-sel-wrap"><Select value={view} onChange={(v) => { setView(v); setExpanded(false); }} options={VIEWS} /></span>
        <span className="an-sel-wrap"><Select value={cat} onChange={(v) => { setCat(v); setExpanded(false); }} options={catOptions(cats)} /></span>
      </div>
      {withRisk && lostParts.length > 0 && (
        <div className="an-muted an-small" style={{ marginBottom: 8 }}>
          По закупке: <b className="an-bad">{lostParts.join(' · ')}</b> — в акцию или в букеты.
          {lost.noPrice > 0 && ` У ${plural(lost.noPrice, 'позиции', 'позиций', 'позиций')} нет закупочной цены — в суммах их нет.`}
        </div>
      )}
      {rows.length ? (
        <>
          <div className="an-scroll-x">
            <table className="an-table an-fixed">
              <colgroup><col style={{ width: '36%' }} /><col style={{ width: '16%' }} /><col className="an-m-hide" style={{ width: '14%' }} /><col className="an-m-hide" style={{ width: '14%' }} />{withRisk && <col style={{ width: '20%' }} />}</colgroup>
              <thead><tr>
                <SortTh k="name" sort={sort} setSort={setSort} className="">Позиция</SortTh>
                <SortTh k="stock" sort={sort} setSort={setSort} hint="Можно продать сейчас. Бронь — уже отложено под оформленные заказы">Свободно</SortTh>
                <SortTh k="days" sort={sort} setSort={setSort} className="r an-m-hide"
                  hint="Свободный остаток ÷ продажи в день за последние 2 недели, вместе с ушедшими в букеты. «—» — за 2 недели не продавалось">Хватит на</SortTh>
                <th className="r an-m-hide">Годен до</th>
                {withRisk && <SortTh k="attention" sort={sort} setSort={setSort} hint="Сколько не продадим до конца срока при продажах последних 2 недель. Срок — от последнего прихода, одна дата на весь остаток. Жёлтым — пропадёт в ближайшие 3 дня">Пропадёт</SortTh>}
              </tr></thead>
              <tbody>
                {shown.map(x => (
                  <tr key={x.key} className={x.expired ? 'an-row-critical' : x.lost > 0 && x.soon ? 'an-row-warning' : ''}>
                    <td><div className="an-ellipsis" title={x.name}>{x.name}</div>
                      <div className="an-muted an-small an-ellipsis">{subline(x.bouquet && 'собранный букет', x.category, x.tag, x.hidden && 'скрыт с витрины', x.idle && 'не продаётся 2 недели')}</div></td>
                    <td className="r">{fmtNum(x.stock)} шт<div className="an-muted an-small">{x.reserved ? `бронь ${fmtNum(x.reserved)}` : ' '}</div></td>
                    <td className="r an-m-hide">{daysText(x.days_left)}</td>
                    <td className="r an-m-hide">{ddmm(x.until) || '—'}</td>
                    {withRisk && (
                      <td className="r">{x.expired ? <span className="an-bad">срок вышел</span> : x.lost ? <span className="an-bad">≈ {fmtNum(x.lost)} шт</span> : '—'}
                        <div className="an-muted an-small">{x.lost_cost ? fmtRub(x.lost_cost) : ' '}</div></td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="an-list-foot">
            <span className="an-muted">Показано {shown.length} из {rows.length}</span>
            {rows.length > COLLAPSED && (
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setExpanded(e => !e)}>
                {expanded ? 'Свернуть' : `Показать все (${rows.length})`}
              </button>
            )}
          </div>
        </>
      ) : <Empty>Под отбор ничего не попало</Empty>}
      <div className="an-muted an-small" style={{ marginTop: 6 }}>Срок — от даты последнего прихода: отдельных партий на складе нет. Перед акцией список стоит проверить глазами.</div>
    </>
  );
}

/* ---------- Списания за период: причины, категории, позиции (владелец 15.10) ---------- */

const WO_SORTS = {
  cost: { get: (x) => x.cost },
  qty: { get: (x) => x.qty },
  name: { get: (x) => x.name },
};

function WriteOffsView({ wo, onNavigate }) {
  const [q, setQ] = useState('');
  const [cat, setCat] = usePersisted('nf_an_wo_cat', '');
  const [sort, setSort] = usePersisted('nf_an_wo_sort', { key: 'cost', dir: 'desc' });
  const [expanded, setExpanded] = useState(false);
  const list = useMemo(() => wo.products || [], [wo.products]);
  const cats = useMemo(() => uniqCats(list), [list]);
  const rows = useMemo(() => {
    const needle = norm(q.trim());
    return sortRows(list.filter(x => (!needle || norm(x.name).includes(needle)) && (!cat || x.category === cat)), sort, WO_SORTS);
  }, [list, q, cat, sort]);
  const shown = expanded ? rows : rows.slice(0, COLLAPSED);
  const sum = rows.reduce((a, x) => ({ cost: a.cost + x.cost, qty: a.qty + x.qty }), { cost: 0, qty: 0 });
  const share = (v) => (wo.total ? fmtPct(v / wo.total * 100, 0) : '—');
  if (!list.length) return <Empty>Списаний за период не было</Empty>;
  return (
    <>
      <div className="an-grid2" style={{ gap: 24, marginBottom: 16 }}>
        <div>
          <div className="an-subtitle" style={{ marginTop: 0 }}>По причинам</div>
          <BarList items={(wo.reasons || []).map(r => ({ label: r.title, value: r.cost, text: `${fmtRub(r.cost)} · ${share(r.cost)}` }))} color="#B0603C" />
        </div>
        <div>
          <div className="an-subtitle" style={{ marginTop: 0 }}>По категориям</div>
          <BarList items={(wo.categories || []).slice(0, 8).map(c => ({ label: c.title, value: c.cost, text: `${fmtRub(c.cost)} · ${share(c.cost)}` }))} color="#B0603C" />
        </div>
      </div>
      <div className="an-filters">
        <input className="form-input an-search" placeholder="Найти позицию" value={q} onChange={(e) => { setQ(e.target.value); setExpanded(false); }} />
        <span className="an-sel-wrap"><Select value={cat} onChange={(v) => { setCat(v); setExpanded(false); }} options={catOptions(cats)} /></span>
      </div>
      {rows.length ? (
        <>
          <div className="an-scroll-x">
            <table className="an-table an-fixed">
              <colgroup><col style={{ width: '50%' }} /><col className="an-m-hide" style={{ width: '16%' }} /><col style={{ width: '20%' }} /><col className="an-m-hide" style={{ width: '14%' }} /></colgroup>
              <thead><tr>
                <SortTh k="name" sort={sort} setSort={setSort} className="">Позиция</SortTh>
                <SortTh k="qty" sort={sort} setSort={setSort} className="r an-m-hide">Списали</SortTh>
                <SortTh k="cost" sort={sort} setSort={setSort} hint="По последней закупочной цене">Сумма</SortTh>
                <th className="r an-m-hide">Доля</th>
              </tr></thead>
              <tbody>
                <tr className="an-total-row">
                  <td>Всего по отбору<div className="an-muted an-small">{plural(rows.length, 'позиция', 'позиции', 'позиций')}</div></td>
                  <td className="r an-m-hide">{fmtNum(sum.qty)} шт</td>
                  <td className="r">{fmtRub(sum.cost)}</td>
                  <td className="r an-m-hide">{share(sum.cost)}</td>
                </tr>
                {shown.map(x => (
                  <tr key={x.product_id}>
                    <td><div className="an-ellipsis" title={x.name}>{x.name}</div>
                      <div className="an-muted an-small an-ellipsis" title={Object.entries(x.reasons || {}).map(([k, v]) => `${k}: ${fmtRub(v)}`).join(' · ')}>
                        {subline(x.category, x.category !== 'Для композиций' && x.tag, x.reason)}</div></td>
                    <td className="r an-m-hide">{fmtNum(x.qty)} шт</td>
                    <td className="r an-b">{fmtRub(x.cost)}</td>
                    <td className="r an-m-hide">{share(x.cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="an-list-foot">
            <span className="an-muted">Показано {shown.length} из {rows.length}</span>
            {rows.length > COLLAPSED && (
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setExpanded(e => !e)}>
                {expanded ? 'Свернуть' : `Показать все (${rows.length})`}
              </button>
            )}
          </div>
        </>
      ) : <Empty>Под отбор ничего не попало</Empty>}
      <div className="an-row-between" style={{ marginTop: 10, flexWrap: 'wrap', gap: 8 }}>
        <span className="an-muted an-small">По последней закупочной цене. Брак при приёмке уходит поставщику по рекламации и сюда не входит. Под позицией — главная причина.</span>
        {onNavigate && <button type="button" className="an-link" onClick={() => onNavigate('warehouse', 'writeoffs')}>Все списания по строкам — Склад → Списания</button>}
      </div>
    </>
  );
}

const MODES = [
  { value: 'profit', label: 'Прибыль по позициям' },
  { value: 'stock', label: 'Остатки' },
  { value: 'writeoffs', label: 'Списания' },
];
const MODE_TITLES = { profit: 'Прибыль по позициям', stock: 'Остатки', writeoffs: 'Списания за период' };

export default function ProductsTab({ data, blocks, mode = 'profit', onMode, onNavigate }) {
  const has = (b) => blocks.includes(b);
  const m = MODE_TITLES[mode] ? mode : 'profit';
  const stockMode = m === 'stock';
  const today = new Date().toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
  const intro = {
    profit: `За период ${data.period?.label || ''}: что продали — стебли поштучно, букеты целиком.`,
    stock: `На сегодня, ${today}: что лежит на складе, продаётся ли и что не успеем продать.`,
    writeoffs: `За период ${data.period?.label || ''}: что списали, почему и в каких категориях.`,
  }[m];
  return (
    <div className="an-stack">
      {has('stock_tiles') && <KpiRow tiles={data.tiles} keys={stockMode ? ['stock_value', 'stock_retail'] : ['purchases', 'write_offs']} compare={false} />}
      {has('positions') && (
        <Panel title={MODE_TITLES[m]} aside={<Segmented small value={m} onChange={onMode}
          options={has('write_offs') ? MODES : MODES.filter(x => x.value !== 'writeoffs')} />}>
          <div className="an-muted an-small" style={{ marginBottom: 10 }}>{intro}</div>
          {m === 'stock' ? <StockTable data={data} withRisk={has('shelf_risk')} />
            : m === 'writeoffs' ? <WriteOffsView wo={data.write_offs} onNavigate={onNavigate} />
              : <ProfitTable data={data} />}
        </Panel>
      )}
    </div>
  );
}
